import { OMRTemplate, OMRSection } from './OMRTemplateRegistry';

/**
 * OMREngine.ts
 * Core logic for image processing and bubble detection.
 * Improved with more robust detection, adaptive thresholding,
 * and better error handling.
 */
export class OMREngine {
  private cv: any;

  constructor(cv: any) {
    this.cv = cv;
  }

  /**
   * Processes the captured/uploaded image and extracts results based on the template.
   * Returns both the answers, debug metadata, and a visual debug image.
   */
  async processImage(
    canvas: HTMLCanvasElement,
    template: OMRTemplate
  ): Promise<{ results: Record<string, any[]>, debug: any, debugImage?: string }> {
    let src = this.cv.imread(canvas);

    // 1. Scale down for consistent processing
    const targetWidth = 1000;
    const scale = targetWidth / src.cols;
    const scaledSize = new this.cv.Size(targetWidth, Math.round(src.rows * scale));
    let scaled = new this.cv.Mat();
    this.cv.resize(src, scaled, scaledSize, 0, 0, this.cv.INTER_AREA);

    let gray = new this.cv.Mat();
    let blurred = new this.cv.Mat();
    let thresh = new this.cv.Mat();

    try {
      // Convert to grayscale
      this.cv.cvtColor(scaled, gray, this.cv.COLOR_RGBA2GRAY);

      // Apply Gaussian blur to reduce noise
      const ksize = new this.cv.Size(5, 5);
      this.cv.GaussianBlur(gray, blurred, ksize, 0);

      // Adaptive threshold - more robust than global threshold
      this.cv.adaptiveThreshold(
        blurred, thresh, 255,
        this.cv.ADAPTIVE_THRESH_GAUSSIAN_C,
        this.cv.THRESH_BINARY_INV,
        15, 5
      );

      // 2. Detect corners
      let corners: any[];
      let cornerMethod = 'auto';
      try {
        corners = this.detectCorners(thresh, scaled.cols, scaled.rows);
      } catch (cornerErr: any) {
        // Fallback: use full image boundaries as corners
        console.warn('Corner detection failed, using full image bounds:', cornerErr.message);
        corners = [
          { x: 0, y: 0 },
          { x: scaled.cols, y: 0 },
          { x: 0, y: scaled.rows },
          { x: scaled.cols, y: scaled.rows }
        ];
        cornerMethod = 'fallback-fullimage';
      }

      // 3. Warp perspective
      const warped = this.warpForm(scaled, corners, template.layout.warpWidth || 1000, template.layout.warpHeight || 1400);

      // 4. Create visual debug image
      let visualDebug = warped.clone();
      const red = new this.cv.Scalar(255, 0, 0, 255);
      const green = new this.cv.Scalar(0, 255, 0, 255);
      const blue = new this.cv.Scalar(0, 120, 255, 255);

      // 5. Extract results per section
      const results: Record<string, any[]> = {};
      const sectionStats: any[] = [];

      for (const section of template.layout.sections) {
        // Draw ROI rectangle
        const p1 = new this.cv.Point(section.roi.x, section.roi.y);
        const p2 = new this.cv.Point(section.roi.x + section.roi.width, section.roi.y + section.roi.height);
        this.cv.rectangle(visualDebug, p1, p2, blue, 2);

        // Label
        this.cv.putText(
          visualDebug, section.name,
          new this.cv.Point(section.roi.x, section.roi.y - 5),
          this.cv.FONT_HERSHEY_SIMPLEX, 0.4, red, 1
        );

        const { answers, stats } = this.processSectionDetailed(warped, section, visualDebug, green, red);
        results[section.name] = answers;
        sectionStats.push({ name: section.name, ...stats });
      }

      // Convert debug image to base64
      const debugCanvas = document.createElement('canvas');
      this.cv.imshow(debugCanvas, visualDebug);
      const debugImageData = debugCanvas.toDataURL('image/jpeg', 0.8);

      visualDebug.delete();

      return {
        results,
        debug: {
          corners,
          cornerMethod,
          sectionStats,
          imageSize: { w: scaled.cols, h: scaled.rows }
        },
        debugImage: debugImageData
      };
    } finally {
      src.delete();
      scaled.delete();
      gray.delete();
      blurred.delete();
      thresh.delete();
    }
  }

  /**
   * Detects the 4 corner markers of the optical form.
   * Uses multiple strategies for robustness.
   */
  private detectCorners(thresh: any, imgWidth: number, imgHeight: number): any[] {
    const contours = new this.cv.MatVector();
    const hierarchy = new this.cv.Mat();
    this.cv.findContours(thresh, contours, hierarchy, this.cv.RETR_LIST, this.cv.CHAIN_APPROX_SIMPLE);

    const candidates: any[] = [];

    // Adaptive area thresholds based on image size
    const imgArea = imgWidth * imgHeight;
    const minArea = imgArea * 0.0001; // 0.01% of image
    const maxArea = imgArea * 0.005;  // 0.5% of image

    for (let i = 0; i < contours.size(); ++i) {
      const cnt = contours.get(i);
      const area = this.cv.contourArea(cnt);
      const rect = this.cv.boundingRect(cnt);
      const aspectRatio = rect.width / rect.height;
      const extent = area / (rect.width * rect.height);

      // Markers: roughly square, solid filled
      if (area > minArea && area < maxArea && aspectRatio > 0.5 && aspectRatio < 2.0 && extent > 0.35) {
        const moments = this.cv.moments(cnt);
        if (moments.m00 !== 0) {
          candidates.push({
            x: moments.m10 / moments.m00,
            y: moments.m01 / moments.m00,
            area: area,
            rect: rect
          });
        }
      }
    }

    contours.delete();
    hierarchy.delete();

    if (candidates.length < 4) {
      throw new Error(`Yeterli köşe işareti bulunamadı (${candidates.length}/4).`);
    }

    // Sort candidates into quadrants
    const midX = imgWidth / 2;
    const midY = imgHeight / 2;

    const topLeft = candidates
      .filter(c => c.x < midX && c.y < midY)
      .sort((a, b) => b.area - a.area);
    const topRight = candidates
      .filter(c => c.x >= midX && c.y < midY)
      .sort((a, b) => b.area - a.area);
    const bottomLeft = candidates
      .filter(c => c.x < midX && c.y >= midY)
      .sort((a, b) => b.area - a.area);
    const bottomRight = candidates
      .filter(c => c.x >= midX && c.y >= midY)
      .sort((a, b) => b.area - a.area);

    if (!topLeft[0] || !topRight[0] || !bottomLeft[0] || !bottomRight[0]) {
      const missing = [];
      if (!topLeft[0]) missing.push('Sol Üst');
      if (!topRight[0]) missing.push('Sağ Üst');
      if (!bottomLeft[0]) missing.push('Sol Alt');
      if (!bottomRight[0]) missing.push('Sağ Alt');
      throw new Error(`Köşe bulunamadı: ${missing.join(', ')}`);
    }

    return [topLeft[0], topRight[0], bottomLeft[0], bottomRight[0]];
  }

  /**
   * Corrects the perspective of the form.
   */
  private warpForm(src: any, corners: any[], width: number, height: number): any {
    const srcCoords = this.cv.matFromArray(4, 1, this.cv.CV_32FC2, [
      corners[0].x, corners[0].y,
      corners[1].x, corners[1].y,
      corners[2].x, corners[2].y,
      corners[3].x, corners[3].y
    ]);

    const dstCoords = this.cv.matFromArray(4, 1, this.cv.CV_32FC2, [
      0, 0,
      width, 0,
      0, height,
      width, height
    ]);

    const M = this.cv.getPerspectiveTransform(srcCoords, dstCoords);
    const result = new this.cv.Mat();
    this.cv.warpPerspective(src, result, M, new this.cv.Size(width, height));

    srcCoords.delete();
    dstCoords.delete();
    M.delete();

    return result;
  }

  /**
   * Detailed section processing with improved bubble detection.
   * Also draws detected bubbles on the debug image.
   */
  private processSectionDetailed(
    warped: any,
    section: OMRSection,
    debugImg: any,
    greenColor: any,
    redColor: any
  ): { answers: any[], stats: any } {
    const answers: any[] = [];
    const sectionGray = new this.cv.Mat();
    this.cv.cvtColor(warped, sectionGray, this.cv.COLOR_RGBA2GRAY);

    // Clamp ROI to image bounds
    const roiX = Math.max(0, Math.min(sectionGray.cols - 1, section.roi.x));
    const roiY = Math.max(0, Math.min(sectionGray.rows - 1, section.roi.y));
    const roiW = Math.min(section.roi.width, sectionGray.cols - roiX);
    const roiH = Math.min(section.roi.height, sectionGray.rows - roiY);

    if (roiW <= 0 || roiH <= 0) {
      sectionGray.delete();
      return {
        answers: Array.from({ length: section.grid.rows }, (_, i) => ({
          questionNumber: section.startQuestion + i,
          answer: ''
        })),
        stats: { avgIntensity: 0, count: 0 }
      };
    }

    const roiRect = new this.cv.Rect(roiX, roiY, roiW, roiH);
    const sectionRoi = sectionGray.roi(roiRect);

    const rowH = roiH / section.grid.rows;
    const colW = roiW / section.grid.cols;

    let totalInkFound = 0;
    let questionsFound = 0;
    const options = section.grid.cols === 4
      ? ['A', 'B', 'C', 'D']
      : ['A', 'B', 'C', 'D', 'E'];

    for (let r = 0; r < section.grid.rows; r++) {
      let markedCol = -1;
      let maxIntensity = 0;
      const rowIntensities: number[] = [];

      for (let c = 0; c < section.grid.cols; c++) {
        const bubbleX = Math.round(c * colW);
        const bubbleY = Math.round(r * rowH);

        // Calculate bubble position with centering
        const bw = Math.min(section.grid.bubbleWidth, Math.floor(colW * 0.8));
        const bh = Math.min(section.grid.bubbleHeight, Math.floor(rowH * 0.8));
        const bx = Math.max(0, Math.min(roiW - bw, bubbleX + Math.floor((colW - bw) / 2)));
        const by = Math.max(0, Math.min(roiH - bh, bubbleY + Math.floor((rowH - bh) / 2)));

        if (bx + bw > roiW || by + bh > roiH) {
          rowIntensities.push(0);
          continue;
        }

        try {
          const bubbleRect = new this.cv.Rect(bx, by, bw, bh);
          const bubbleRoi = sectionRoi.roi(bubbleRect);
          const binaryBubble = new this.cv.Mat();
          this.cv.threshold(bubbleRoi, binaryBubble, 0, 255, this.cv.THRESH_BINARY_INV + this.cv.THRESH_OTSU);
          const count = this.cv.countNonZero(binaryBubble);
          const fillRatio = count / (bw * bh);

          rowIntensities.push(fillRatio);

          binaryBubble.delete();
          bubbleRoi.delete();
        } catch {
          rowIntensities.push(0);
        }
      }

      // Dynamic threshold: a bubble is "marked" if its fill ratio is
      // significantly above the row average AND above a minimum threshold
      const avgFill = rowIntensities.reduce((a, b) => a + b, 0) / rowIntensities.length;
      const fillThreshold = Math.max(0.30, avgFill * 1.5);

      for (let c = 0; c < rowIntensities.length; c++) {
        if (rowIntensities[c] > fillThreshold && rowIntensities[c] > maxIntensity) {
          // Check if this is clearly the darkest bubble (at least 1.3x the second darkest)
          const sorted = [...rowIntensities].sort((a, b) => b - a);
          if (sorted[0] > 0 && (sorted.length < 2 || sorted[0] > sorted[1] * 1.2)) {
            if (rowIntensities[c] === sorted[0]) {
              maxIntensity = rowIntensities[c];
              markedCol = c;
            }
          } else if (rowIntensities[c] > 0.45) {
            // High confidence: clearly filled
            maxIntensity = rowIntensities[c];
            markedCol = c;
          }
        }
      }

      // Draw marker on debug image
      if (markedCol !== -1 && debugImg) {
        const drawX = roiX + Math.round(markedCol * colW + colW / 2);
        const drawY = roiY + Math.round(r * rowH + rowH / 2);
        try {
          this.cv.circle(debugImg, new this.cv.Point(drawX, drawY), 6, greenColor, 2);
        } catch { /* ignore drawing errors */ }
      }

      if (markedCol !== -1) {
        answers.push({
          questionNumber: section.startQuestion + r,
          answer: options[markedCol] || '',
          confidence: maxIntensity
        });
        totalInkFound += maxIntensity;
        questionsFound++;
      } else {
        answers.push({
          questionNumber: section.startQuestion + r,
          answer: '',
          confidence: 0
        });
      }
    }

    const stats = {
      avgIntensity: questionsFound > 0 ? Math.round((totalInkFound / questionsFound) * 100) : 0,
      count: questionsFound,
      total: section.grid.rows
    };

    sectionGray.delete();
    sectionRoi.delete();

    return { answers, stats };
  }
}
